import { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import {
  updateProfile,
  updateEmail,
  updatePassword,
} from "firebase/auth";
import { doc, getDoc, updateDoc, collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";

export default function Dashboard() {
  const [userData, setUserData] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Trail management states for editors
  const [trails, setTrails] = useState([]);
  const [loadingTrails, setLoadingTrails] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      setUser(u);
    });
    return unsub;
  }, []);

  useEffect(() => {
    async function loadUser() {
      if (!user) return;

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setUserData({ uid: user.uid, ...data });
          setName(data.name || "");
          setEmail(user.email);

          // If user is an editor, load their trails
          if (data.role === "editor") {
            loadEditorTrails(data.allowedCounties || []);
          }
        }
      } catch (error) {
        console.error("Error loading user:", error);
      }
    }

    loadUser();
  }, [user]);

  async function loadEditorTrails(allowedCounties) {
    if (allowedCounties.length === 0) return;

    try {
      setLoadingTrails(true);
      const trailsRef = collection(db, "trails");
      const allTrails = await getDocs(trailsRef);
      
      // Filter trails by:
      // 1. Allowed counties
      // 2. Created by this editor (createdBy === user.uid)
      const filtered = allTrails.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(trail => 
          allowedCounties.includes(trail.city) && 
          trail.createdBy === user.uid
        );
      
      setTrails(filtered);
    } catch (error) {
      console.error("Error loading trails:", error);
    } finally {
      setLoadingTrails(false);
    }
  }

  function checkStrength(pass) {
    if (!pass) return "";
    if (pass.length < 6) return "Weak";
    if (/[A-Z]/.test(pass) && /\d/.test(pass) && pass.length >= 8)
      return "Strong";
    return "Medium";
  }

  useEffect(() => {
    setPasswordStrength(checkStrength(newPassword));
  }, [newPassword]);

  async function updateUserInfo(e) {
    if (!user) {
      setMessage("User not loaded yet.");
      return;
    }

    e.preventDefault();
    setMessage("");
    setIsLoading(true);

    try {
      if (name !== user.displayName) {
        await updateProfile(user, { displayName: name });
      }

      if (email !== user.email) {
        await updateEmail(user, email);
      }

      await updateDoc(doc(db, "users", user.uid), {
        name,
        email,
      });

      setMessage("Profile updated successfully!");
    } catch (err) {
      console.error(err);
      setMessage("Error updating profile: " + err.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handlePasswordUpdate(e) {
    if (!user) {
      setMessage("User not loaded yet.");
      return;
    }

    e.preventDefault();
    setMessage("");

    if (passwordStrength === "Weak") {
      setMessage("Password is too weak.");
      return;
    }

    setIsLoading(true);

    try {
      await updatePassword(user, newPassword);
      setNewPassword("");
      setMessage("Password updated successfully!");
    } catch (err) {
      console.error(err);
      setMessage("Error updating password: " + err.message);
    } finally {
      setIsLoading(false);
    }
  }

  if (!userData) {
    return (
      <div className="dash-page">
        <div className="dash-loader">
          <div className="spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const isVerified = !!userData;
  const isEditor = userData?.role === "editor";
  const isAdmin = userData?.role === "admin";
  const allowedCounties = userData?.allowedCounties || [];

  return (
    <div className="dash-page">
      <div className="dash-container">
        <header className="dash-header">
          <div className="dash-header-content">
            <h1 className="dash-title">Account Settings</h1>
            <p className="dash-subtitle">
              {isEditor 
                ? `Content Editor • Managing ${allowedCounties.join(", ")}`
                : "Manage your profile and security preferences"
              }
            </p>
          </div>
          {isAdmin && (
            <a href="/admin" className="admin-badge">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 1L10.5 6L16 7L12 11L13 16L8 13.5L3 16L4 11L0 7L5.5 6L8 1Z"
                  fill="currentColor"
                />
              </svg>
              Admin Panel
            </a>
          )}
          {isEditor && (
            <div className="editor-badge">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M11.5 1L14 3.5L5.5 12H3V9.5L11.5 1Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              </svg>
              Content Editor
            </div>
          )}
        </header>

        <div className="dash-user-info">
          <div className="user-avatar">
            {name.charAt(0).toUpperCase() || "U"}
          </div>
          <div className="user-details">
            <h2>{name || "User"}</h2>
            <p>{user?.email}</p>
          </div>
          {isVerified && (
            <div className="verified-badge">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 0L12.5 7.5L20 10L12.5 12.5L10 20L7.5 12.5L0 10L7.5 7.5L10 0Z"
                  fill="currentColor"
                />
              </svg>
              Verified
            </div>
          )}
        </div>

        <div className="dash-grid">
          <div className="dash-card">
            <div className="card-header">
              <h2>Profile Information</h2>
              <p>Update your account details</p>
            </div>

            <form onSubmit={updateUserInfo} className="dash-form">
              <div className="form-group">
                <label htmlFor="name">Display Name</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <button
                type="submit"
                className="dash-btn primary"
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
            </form>
          </div>

          <div className="dash-card">
            <div className="card-header">
              <h2>Security</h2>
              <p>Update your password</p>
            </div>

            <form onSubmit={handlePasswordUpdate} className="dash-form">
              <div className="form-group">
                <label htmlFor="password">New Password</label>
                <input
                  id="password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              </div>

              {passwordStrength && (
                <div className="password-strength">
                  <div className="strength-label">
                    Password Strength: {passwordStrength}
                  </div>
                  <div className="strength-bar">
                    <div
                      className={`strength-fill ${passwordStrength.toLowerCase()}`}
                    ></div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="dash-btn primary"
                disabled={isLoading || passwordStrength === "Weak"}
              >
                {isLoading ? "Updating..." : "Update Password"}
              </button>
            </form>
          </div>
        </div>

        {message && (
          <div className={`dash-message ${message.includes("Error") ? "error" : "success"}`}>
            <div className="message-icon">
              {message.includes("Error") ? "✕" : "✓"}
            </div>
            <p>{message}</p>
          </div>
        )}

        {/* Trail Management Section - Only for Editors */}
        {isEditor && (
          <section className="trails-management-section">
            <div className="section-header">
              <div>
                <h2>Trail Management</h2>
                <p className="section-subtitle">
                  Manage trails in your assigned counties: {allowedCounties.join(", ")}
                </p>
              </div>
              <button
                className="dash-btn primary"
                onClick={() => navigate("/trailseditor/new")}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add New Trail
              </button>
            </div>

            {loadingTrails ? (
              <div className="trails-loader">
                <div className="spinner"></div>
                <p>Loading your trails...</p>
              </div>
            ) : trails.length === 0 ? (
              <div className="empty-trails">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>No trails yet in your counties</p>
                <button
                  className="dash-btn primary"
                  onClick={() => navigate("/trailseditor/new")}
                >
                  Create Your First Trail
                </button>
              </div>
            ) : (
              <div className="trails-grid">
                {trails.map((trail) => (
                  <div key={trail.id} className="trail-card">
                    <div className="trail-header">
                      <h3>{trail.name}</h3>
                      {trail.isPaid ? (
                        <span className="trail-badge paid">
                          Paid • €{trail.priceEUR || 0}
                        </span>
                      ) : (
                        <span className="trail-badge free">Free</span>
                      )}
                    </div>

                    <p className="trail-description">{trail.description}</p>

                    <div className="trail-meta">
                      <div className="meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <span>{trail.city}</span>
                      </div>
                      <div className="meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <span>{trail.mapType}</span>
                      </div>
                    </div>

                    <div className="trail-status">
                      <span className={`status-badge ${trail.isLaunched ? 'live' : 'offline'}`}>
                        <span className="status-indicator"></span>
                        {trail.isLaunched ? 'Live' : 'Offline'}
                      </span>
                    </div>

                    <div className="trail-actions">
                      <button
                        className="dash-btn secondary"
                        onClick={() => navigate(`/trailseditor/${trail.id}/clues`)}
                      >
                        Manage Clues
                      </button>
                      <button
                        className="dash-btn primary"
                        onClick={() => navigate(`/trailseditor/${trail.id}/edit`)}
                      >
                        Edit Trail
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}